/**
 * The tab host shared by every hierarchy detail screen.
 *
 * Built by f6.1 inside Client detail and extracted here UNCHANGED by f15.1,
 * when the Business Unit Group and Business Unit screens gained a second tab
 * (`Artifacts`). The strip and the panels are rendered from a declared array
 * of descriptors -- adding a tab is one entry, nothing else.
 *
 * `count` is optional and deliberately so: an ABSENT badge says nothing, while
 * a zero claims the tab's collection is empty.
 */

import { useState, type ReactNode } from 'react';

export interface TabDescriptor {
  id: string;
  label: string;
  icon?: ReactNode;
  count?: number;
  panel: ReactNode;
}

/**
 * The tab host. It may be CONTROLLED -- `activeId` and `onSelect` -- which is
 * how Client detail keeps the active tab in `?tab=<id>`. Uncontrolled, it
 * keeps its own state. An unknown or absent id selects the first tab.
 */
export function TabHost({
  label,
  tabs,
  activeId: controlledId,
  onSelect,
}: {
  /** What the strip is called, for assistive technology. */
  label: string;
  tabs: readonly TabDescriptor[];
  activeId?: string | null;
  onSelect?: (id: string) => void;
}) {
  const [ownId, setOwnId] = useState(tabs[0]?.id ?? '');
  const activeId = onSelect ? controlledId : ownId;
  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0];
  const select = onSelect ?? setOwnId;

  return (
    <>
      <div className="tabs" role="tablist" aria-label={label}>
        {tabs.map((tab) => {
          const selected = tab.id === active?.id;
          return (
            <button
              type="button"
              key={tab.id}
              id={`tab-${tab.id}`}
              role="tab"
              aria-selected={selected}
              aria-controls={`panel-${tab.id}`}
              className={selected ? 'tab is-active' : 'tab'}
              onClick={() => select(tab.id)}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && <span className="count">{tab.count}</span>}
            </button>
          );
        })}
      </div>
      {active && (
        <div id={`panel-${active.id}`} role="tabpanel" aria-labelledby={`tab-${active.id}`}>
          {active.panel}
        </div>
      )}
    </>
  );
}
